import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useAuth } from "./App";
import ContestHome from "./ContestHome";
import ContestDetail from "./ContestDetail";
import AdminPanel from "./AdminPanel";
import HowToPlay from "./HowToPlay";
import ScoreTicker from "./ScoreTicker";
import logo from "./logo.png";

const ADMIN_UID = "fy1HRZav9TW5MJFxHE1hc3mJFUl2";

function WCHeader({ onHowToPlay }) {
  const navigate = useNavigate();
  const { displayName, logout } = useAuth();

  return (
    <header style={{
      background: "linear-gradient(135deg, #0a1628 0%, #112240 50%, #0a1628 100%)",
      borderBottom: "3px solid #FFD700",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: "repeating-linear-gradient(0deg, white 0px, white 2px, transparent 2px, transparent 20px), repeating-linear-gradient(90deg, white 0px, white 2px, transparent 2px, transparent 20px)",
        pointerEvents: "none",
      }} />

      <div style={{ height: 4, background: "linear-gradient(90deg, #E53935 0%, #FFD700 33%, #00C853 66%, #E53935 100%)" }} />

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "10px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        {/* Back to platform */}
        <button
          onClick={() => navigate("/")}
          className="btn btn-ghost btn-sm"
          style={{ flexShrink: 0 }}
        >
          ← All Games
        </button>

        <img
          src={logo}
          alt="World Cup Pool 2026"
          style={{ height: 72, width: "auto", objectFit: "contain", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))", flexShrink: 0 }}
        />

        <div className="ticker-desktop" style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
          <ScoreTicker inline />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <button onClick={onHowToPlay} className="btn btn-ghost btn-sm">❓ How to Play</button>
          {displayName && (
            <>
              <button onClick={() => navigate("/account")} className="btn btn-ghost btn-sm hide-mobile">
                👤 {displayName}
              </button>
              <button onClick={logout} className="btn btn-ghost btn-sm">Sign Out</button>
            </>
          )}
        </div>
      </div>

      <div className="ticker-mobile" style={{ padding: "0 12px 8px" }}>
        <ScoreTicker inline />
      </div>

      <div style={{ height: 3, background: "linear-gradient(90deg, #00C853, #FFD700, #E53935, #00C853)" }} />

      <style>{`
        .ticker-desktop { display: flex; }
        .ticker-mobile  { display: none; }
        @media (max-width: 600px) {
          .ticker-desktop { display: none; }
          .ticker-mobile  { display: block; }
        }
      `}</style>
    </header>
  );
}

export default function WCPool() {
  const { user } = useAuth();
  const [activeContest, setActiveContest] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  // Preserve existing deep link behavior (?join=CODE and ?view=CODE)
  const [autoJoinCode] = useState(() => new URLSearchParams(window.location.search).get("join") || "");
  const [deepLinkViewCode] = useState(() => new URLSearchParams(window.location.search).get("view") || "");

  useEffect(() => {
    if (!deepLinkViewCode || !user || activeContest) return;
    let cancelled = false;
    (async () => {
      try {
        const q = query(collection(db, "contests"), where("joinCode", "==", deepLinkViewCode.toUpperCase().trim()));
        const snap = await getDocs(q);
        if (!cancelled && !snap.empty) {
          const d = snap.docs[0];
          setActiveContest({ id: d.id, ...d.data() });
        }
      } catch (e) { console.error("Deep link load failed:", e); }
    })();
    return () => { cancelled = true; };
  }, [deepLinkViewCode, user, activeContest]);

  return (
    <div style={{ minHeight: "100vh" }}>
      {showHowToPlay && <HowToPlay onClose={() => setShowHowToPlay(false)} />}
      <WCHeader onHowToPlay={() => setShowHowToPlay(true)} />

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
        {!activeContest && (
          <ContestHome
            user={user}
            onEnterContest={setActiveContest}
            autoJoinCode={autoJoinCode}
          />
        )}
        {activeContest && (
          <ContestDetail
            user={user}
            contest={activeContest}
            onLeave={() => setActiveContest(null)}
          />
        )}
        {user?.uid === ADMIN_UID && <AdminPanel user={user} />}
      </div>
    </div>
  );
}
